import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../../../../assets/icons/index';

export type MappingComboboxOption = {
  disabled?: boolean;
  label: string;
  value: string;
};

type MappingComboboxProps = {
  disabled?: boolean;
  onChange: (nextValue: string) => void;
  options: MappingComboboxOption[];
  placeholder?: string;
  value: string;
};

export const MappingCombobox: FC<MappingComboboxProps> = ({
  disabled = false,
  onChange,
  options,
  placeholder,
  value,
}) => {
  const selectedOption = options.find((option) => option.value === value) ?? null;

  return (
    <Combobox disabled={disabled} onChange={(next) => onChange(next ?? '')} value={value}>
      {({ open }) => (
        <div className="relative w-full overflow-visible">
          <ComboboxInput
            className={twMerge(
              'relative z-10 h-10 w-full cursor-pointer rounded-t border-b border-gray-700 bg-gray-50 py-0 pl-2.5 pr-9 text-left text-sm outline-none transition-colors',
              disabled && 'cursor-not-allowed text-gray-400',
              open ? 'border-b-2 border-brand-700' : 'hover:border-gray-900',
              selectedOption ? 'text-[#1E293B]' : 'text-[#475569]',
            )}
            displayValue={(item) => {
              if (!item) return '';
              return options.find((option) => option.value === item)?.label ?? '';
            }}
            placeholder={placeholder}
            readOnly
          />
          <ComboboxButton className="absolute inset-x-0 top-0 z-20 h-10 rounded-t" type="button" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex items-center pr-2.5">
            <ChevronDownIcon
              className={twMerge(
                'h-4 w-4 transition-transform duration-200',
                open
                  ? 'rotate-180 text-[#2563EB]'
                  : selectedOption
                    ? 'text-green-600'
                    : 'text-[#64748B]',
              )}
            />
          </div>
          {open && !disabled && (
            <ComboboxOptions
              anchor={{ gap: 4, to: 'bottom start' }}
              className={twMerge(
                'pointer-events-auto z-[10001] flex flex-col rounded-lg border border-gray-200 bg-white shadow-2xl outline-none',
                'w-[var(--input-width)]',
              )}
              onMouseEnter={(e) => {
                e.currentTarget.focus();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              portal={false}
              static
            >
              <div
                className="scrollbar-always-visible max-h-[200px] overflow-y-auto overscroll-contain py-1"
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                {options.map((option) => (
                  <ComboboxOption
                    className={twMerge(
                      'px-4 py-2.5 text-sm',
                      option.disabled
                        ? 'cursor-not-allowed text-gray-400'
                        : 'cursor-pointer text-[#334155] data-[focus]:bg-[#F1F5F9] data-[focus]:text-[#1E293B]',
                    )}
                    disabled={option.disabled}
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </ComboboxOption>
                ))}
              </div>
            </ComboboxOptions>
          )}
        </div>
      )}
    </Combobox>
  );
};
